import { useMemo } from "react";
import type { ActivityArea } from "@shared/domain";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import type {
  PaymentMethodsDTO,
  CheckPlanDTO,
  CheckRulesDTO,
  ChecklistTypeDTO,
  MyChecksDTO,
  RoomSetupDTO,
  StarterDTO,
  TypeChecklistDTO,
  ActivityDTO,
  AddonDTO,
  MealPlanDTO,
  BookingDTO,
  CalendarDTO,
  ConflictDTO,
  DashboardDTO,
  GuestDetailDTO,
  GuestRowDTO,
  MeDTO,
  PropertyDTO,
  RatesDTO,
  RoomDTO,
  RoomTypeDTO,
  SearchDTO,
  UserRowDTO,
  LocationDTO,
  NumberedPage,
  Page,
  TaxDTO,
  PerformanceReportDTO,
  ChannelReportDTO,
  CancellationReportDTO,
  IssueCountsDTO,
  IssueDetailDTO,
  IssueRowDTO,
  SpendDetailDTO,
  SpendRowDTO,
  SpendSummaryDTO,
  PlanDTO,
  HandoverNoteDTO,
  LostItemDTO,
  LostItemDetailDTO,
  ChannelManagerDTO,
  ChannelDTO,
  AccessDTO,
  AccessHistoryDTO,
  RoomHistoryDTO,
  TaskDetailDTO,
  RoomDayDTO,
  CheckTaskDTO,
  LaundryDTO,
  LaundryBatchDTO,
  InventoryDTO,
} from "@shared/types";
import { can, type Permission } from "@shared/domain";
import { useUi } from "@/stores/ui";
import { api, qs } from "./api";
import { endpoints } from "./endpoints";
import { queryKeys } from "./queryKeys";

/* ── Session & scope ── */

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me.all,
    queryFn: () => api<MeDTO>(endpoints.auth.me, { quiet401: true }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** The signed-in user; only call inside the authenticated shell. */
export function useUser(): MeDTO {
  const { data } = useMe();
  if (!data) throw new Error("useUser outside the signed-in shell");
  return data;
}

export function useCan(p: Permission): boolean {
  const { data } = useMe();
  return !!data && can(data, p);
}

/**
 * The active property scope. `property` is set when one property is in view;
 * pages that need exactly one (search, rates, quick entry) fall back to the first.
 */
export function useScope() {
  const me = useUser();
  const scope = useUi((s) => s.scope);
  const properties = me.properties;
  const valid = scope === "all" ? (properties.length > 1 ? "all" : (properties[0]?.id ?? "all")) : properties.some((p) => p.id === scope) ? scope : properties.length > 1 ? "all" : (properties[0]?.id ?? "all");
  const property = valid === "all" ? null : (properties.find((p) => p.id === valid) ?? null);
  return {
    scope: valid,
    property,
    /** One property to act on when the page can't do a roll-up. */
    primary: property ?? properties[0] ?? null,
    properties,
    label: property ? property.shortName : properties.length ? "All properties" : "No property yet",
  };
}

/* ── Paged lists ── */

/** How many rows a list screen asks for at a time. */
export const PAGE_SIZE = 50;

/**
 * A server list read a page at a time. `items` is every page loaded so far,
 * flattened; the rest (`hasNextPage`, `fetchNextPage`, …) is TanStack's own.
 * `path(cursor)` builds the request for one page.
 */
export function usePaged<T>(queryKey: QueryKey, path: (cursor: string | undefined) => string, opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  const q = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => api<Page<T>>(path(pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: opts.enabled,
    refetchInterval: opts.refetchInterval,
  });
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  return { ...q, items };
}

/* ── Reads ── */

export const useDashboard = (scope: string) =>
  useQuery({ queryKey: queryKeys.dashboard.scope(scope), queryFn: () => api<DashboardDTO>(`${endpoints.dashboard}${qs({ propertyId: scope })}`), refetchInterval: 30_000 });

export const useCalendar = (scope: string, from: string, to: string) =>
  useQuery({
    queryKey: queryKeys.calendar.range(scope, from, to),
    queryFn: () => api<CalendarDTO>(`${endpoints.calendar}${qs({ propertyId: scope, from, to })}`),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

export const useSearch = (propertyId: string | undefined, checkIn: string, checkOut: string, adults?: number, enabled = true, beds?: number) =>
  useQuery({
    queryKey: queryKeys.search.stay(propertyId ?? "", checkIn, checkOut, adults, beds),
    queryFn: () => api<SearchDTO>(`${endpoints.availability}${qs({ propertyId, checkIn, checkOut, adults, beds })}`),
    enabled: enabled && !!propertyId && checkOut > checkIn,
    placeholderData: keepPreviousData,
  });

export const useMealPlans = (propertyId: string | undefined) =>
  useQuery({ queryKey: queryKeys.mealPlans.property(propertyId ?? ""), queryFn: () => api<MealPlanDTO[]>(`${endpoints.mealPlans}${qs({ propertyId })}`), enabled: !!propertyId, staleTime: 5 * 60_000 });

export const useAddons = (propertyId: string | undefined) =>
  useQuery({ queryKey: queryKeys.addons.property(propertyId ?? ""), queryFn: () => api<AddonDTO[]>(`${endpoints.addons}${qs({ propertyId })}`), enabled: !!propertyId, staleTime: 5 * 60_000 });

/** Who can be a booking's point of contact. */
export const useTeam = (scope: string) =>
  useQuery({ queryKey: queryKeys.team.scope(scope), queryFn: () => api<{ id: string; name: string; role: string }[]>(`${endpoints.team}${qs({ propertyId: scope })}`), staleTime: 5 * 60_000 });

export const useBooking = (ref: string | undefined) =>
  useQuery({ queryKey: queryKeys.booking.detail(ref ?? ""), queryFn: () => api<BookingDTO>(endpoints.bookings.detail(ref!)), enabled: !!ref });

export const useBookingHistory = (ref: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: queryKeys.booking.history(ref ?? ""), queryFn: () => api<ActivityDTO[]>(endpoints.bookings.history(ref!)), enabled: !!ref && enabled });

export const useConflicts = (scope: string, status?: "open" | "resolved", enabled = true) =>
  usePaged<ConflictDTO>(queryKeys.conflicts.list(scope, status), (cursor) => `${endpoints.conflicts.list}${qs({ propertyId: scope, status, cursor, limit: PAGE_SIZE })}`, {
    enabled,
    refetchInterval: 30_000,
  });

/** The nav badge: a number from the server, not a list counted here. */
export const useOpenConflictCount = (scope: string) =>
  useQuery({
    queryKey: queryKeys.conflicts.count(scope),
    queryFn: () => api<{ open: number }>(`${endpoints.conflicts.count}${qs({ propertyId: scope })}`),
    refetchInterval: 30_000,
    select: (d) => d.open,
  });

export const useRoomTypes = (propertyId: string | undefined) =>
  useQuery({ queryKey: queryKeys.roomTypes.property(propertyId ?? ""), queryFn: () => api<RoomTypeDTO[]>(`${endpoints.roomTypes.list}${qs({ propertyId })}`), enabled: !!propertyId });

export const useRates = (propertyId: string | undefined, roomTypeId: string | undefined, from: string, to: string) =>
  useQuery({
    queryKey: queryKeys.rates.range(propertyId ?? "", roomTypeId ?? "", from, to),
    queryFn: () => api<RatesDTO>(`${endpoints.rates}${qs({ propertyId, roomTypeId, from, to })}`),
    enabled: !!propertyId && !!roomTypeId,
    placeholderData: keepPreviousData,
  });

/** One numbered page of a server table, holding the last page on screen while the next loads. */
export function useNumbered<T>(queryKey: QueryKey, path: string) {
  return useQuery({ queryKey, queryFn: () => api<NumberedPage<T>>(path), placeholderData: keepPreviousData });
}

export type GuestParams = { propertyId: string; q?: string; archived?: "1"; repeat?: "1"; sort?: string; dir?: string; page: number; limit: number };
export const useGuests = (p: GuestParams) => useNumbered<GuestRowDTO>(queryKeys.guests.list(p), `${endpoints.guests.list}${qs(p)}`);

export const useGuest = (id: string | undefined) =>
  useQuery({ queryKey: queryKeys.guest.detail(id ?? ""), queryFn: () => api<GuestDetailDTO>(endpoints.guests.detail(id!)), enabled: !!id });

export const useLocations = () => useQuery({ queryKey: queryKeys.locations.all, queryFn: () => api<LocationDTO[]>(endpoints.locations.list), staleTime: 5 * 60_000 });

export type UserParams = { q?: string; status?: "active" | "invited" | "disabled"; role?: string; sort?: string; dir?: string; page: number; limit: number };
export const useUsers = (p: UserParams) => useNumbered<UserRowDTO>(queryKeys.users.list(p), `${endpoints.users.list}${qs(p)}`);

export type ActivityParams = {
  propertyId: string;
  q?: string;
  actorId?: string;
  kind?: string;
  from?: string;
  to?: string;
  dir?: string;
  /** An Area chip: Bookings, Room checks, Repairs… */
  area?: ActivityArea;
  /** One thing's own history. */
  entityType?: string;
  entityId?: string;
  page: number;
  limit: number;
};
export const useActivity = (p: ActivityParams) => useNumbered<ActivityDTO>(queryKeys.activity.feed(p), `${endpoints.activity}${qs(p)}`);

/** The business's payment methods, and which this property takes. Rarely changes. */
export const usePaymentMethods = (propertyId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.paymentMethods.of(propertyId ?? ""),
    queryFn: () => api<PaymentMethodsDTO>(`${endpoints.paymentMethods}${qs({ propertyId })}`),
    enabled: !!propertyId,
    staleTime: 5 * 60_000,
  });

/** How many entries each Area chip holds, under the other filters. */
export const useActivityAreas = (p: Omit<ActivityParams, "area" | "page" | "limit" | "dir">) =>
  useQuery({
    queryKey: queryKeys.activity.areas(p),
    queryFn: () => api<{ all: number } & Record<ActivityArea, number>>(`${endpoints.activityAreas}${qs(p)}`),
    placeholderData: keepPreviousData,
  });

export const useSettings = (propertyId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.settings.property(propertyId ?? ""),
    queryFn: () => api<{ property: PropertyDTO }>(`${endpoints.settings}${qs({ propertyId })}`),
    enabled: !!propertyId,
  });

/** Managers only: the server refuses anyone who can't change settings. */
export const useChannelManager = (propertyId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: queryKeys.channelManager.property(propertyId ?? ""),
    queryFn: () => api<ChannelManagerDTO>(`${endpoints.channelManager.detail}${qs({ propertyId })}`),
    enabled: !!propertyId && enabled,
  });

export const useInventory = (propertyId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.inventory.property(propertyId ?? ""),
    queryFn: () => api<InventoryDTO>(endpoints.properties.inventory(propertyId!)),
    enabled: !!propertyId,
  });

export const useRooms = (propertyId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.rooms.property(propertyId ?? ""),
    queryFn: () => api<RoomDTO[]>(`${endpoints.rooms.list}${qs({ propertyId })}`),
    enabled: !!propertyId,
  });

/** Booking sources. Every role reads them: chips everywhere are coloured by them. They change rarely. */
export const useChannels = () => useQuery({ queryKey: queryKeys.channels.all, queryFn: () => api<ChannelDTO[]>(endpoints.channels.list), staleTime: 10 * 60_000 });

/** Whether this property's listed prices already include its taxes. */
export function useTaxInclusive(propertyId: string | null | undefined): boolean {
  const { data } = useMe();
  return !!data?.properties.find((p) => p.id === propertyId)?.taxInclusive;
}

/** A property's taxes. Every role reads them: the desk quotes totals. */
export const useTaxes = (propertyId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.taxes.property(propertyId ?? ""),
    queryFn: () => api<TaxDTO[]>(`${endpoints.taxes.list}${qs({ propertyId })}`),
    enabled: !!propertyId,
    staleTime: 5 * 60_000,
  });

type ReportKind = "performance" | "channels" | "cancellations";
type ReportOf<K extends ReportKind> = K extends "performance" ? PerformanceReportDTO : K extends "channels" ? ChannelReportDTO : CancellationReportDTO;

/** An owner report. Figures move slowly; no polling. */
export const useReport = <K extends ReportKind>(kind: K, scope: string, from: string, to: string, ownership?: "owned" | "leased") =>
  useQuery({
    queryKey: queryKeys.reports.one(kind, scope, from, to, ownership),
    queryFn: () => api<ReportOf<K>>(`${endpoints.reports[kind]}${qs({ propertyId: scope, from, to, ownership })}`),
    enabled: from <= to,
    placeholderData: keepPreviousData,
    refetchInterval: false,
  });

/* ── Writes ── */

/** A mutation that refreshes everything once it lands: bookings touch every screen. */
export function useApiMutation<TVars, TResult = unknown>(fn: (v: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries() });
}

/* ── Operations: issues & spend ── */

export type OpenIssueParams = { propertyId: string; q?: string; mine?: "1" };
export const useOpenIssues = (p: OpenIssueParams) =>
  useQuery({
    queryKey: queryKeys.issues.open(p),
    queryFn: () => api<IssueRowDTO[]>(`${endpoints.issues.open}${qs(p)}`),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

export type ClosedIssueParams = { propertyId: string; q?: string; sort?: string; dir?: string; page: number; limit: number };
export const useClosedIssues = (p: ClosedIssueParams, enabled = true) =>
  useQuery({
    queryKey: queryKeys.issues.closed(p),
    queryFn: () => api<NumberedPage<IssueRowDTO>>(`${endpoints.issues.closed}${qs(p)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useIssueCounts = (scope: string) =>
  useQuery({
    queryKey: queryKeys.issues.counts(scope),
    queryFn: () => api<IssueCountsDTO>(`${endpoints.issues.counts}${qs({ propertyId: scope })}`),
    refetchInterval: 60_000,
  });

export const useIssue = (id: string | undefined) =>
  useQuery({ queryKey: queryKeys.issues.detail(id ?? ""), queryFn: () => api<IssueDetailDTO>(endpoints.issues.detail(id!)), enabled: !!id });

export type SpendParams = {
  propertyId: string;
  from?: string;
  to?: string;
  q?: string;
  category?: string;
  paidBy?: string;
  reimbursement?: string;
  source?: string;
  voided?: "1";
  sort?: string;
  dir?: string;
  page: number;
  limit: number;
};
export const useSpends = (p: SpendParams) => useNumbered<SpendRowDTO>(queryKeys.spends.list(p), `${endpoints.spends.list}${qs(p)}`);

export const useSpendSummary = (p: { propertyId: string; from?: string; to?: string }) =>
  useQuery({
    queryKey: queryKeys.spends.summary(p),
    queryFn: () => api<SpendSummaryDTO>(`${endpoints.spends.summary}${qs(p)}`),
    placeholderData: keepPreviousData,
  });

export const useSpend = (id: string | undefined) =>
  useQuery({ queryKey: queryKeys.spends.detail(id ?? ""), queryFn: () => api<SpendDetailDTO>(endpoints.spends.detail(id!)), enabled: !!id });

export const useSuppliers = (scope: string) =>
  useQuery({
    queryKey: queryKeys.spends.suppliers(scope),
    queryFn: () => api<string[]>(`${endpoints.spends.suppliers}${qs({ propertyId: scope })}`),
    staleTime: 5 * 60_000,
  });

export const usePlans = (scope: string) => useQuery({ queryKey: queryKeys.plans.scope(scope), queryFn: () => api<PlanDTO[]>(`${endpoints.plans.list}${qs({ propertyId: scope })}`) });

export const useHandover = (scope: string, date: string) =>
  useQuery({
    queryKey: queryKeys.handover.day(scope, date),
    queryFn: () => api<HandoverNoteDTO[]>(`${endpoints.handover.list}${qs({ propertyId: scope, date })}`),
    refetchInterval: 60_000,
  });

export type LostParams = { propertyId: string; status?: string; q?: string; page: number; limit: number };
export const useLostItems = (p: LostParams) => useNumbered<LostItemDTO>(queryKeys.lost.list(p), `${endpoints.lost.list}${qs(p)}`);
export const useLostItem = (id: string | undefined) =>
  useQuery({ queryKey: queryKeys.lost.detail(id ?? ""), queryFn: () => api<LostItemDetailDTO>(endpoints.lost.detail(id!)), enabled: !!id });

/* ── Upkeep ── */

/** The course-bar badge. */
export const useUpkeepCount = (scope: string) =>
  useQuery({
    queryKey: queryKeys.upkeep.count(scope),
    queryFn: () => api<{ n: number; open: number; review: number; warned: number }>(`${endpoints.upkeep.count}${qs({ propertyId: scope })}`),
    refetchInterval: 60_000,
  });

export const useRoomDay = (roomId: string, date = "") =>
  useQuery({ queryKey: queryKeys.upkeep.room(roomId, date), queryFn: () => api<RoomDayDTO>(`${endpoints.upkeep.roomDay(roomId)}${qs({ date: date || undefined })}`), refetchInterval: 30_000 });

/** A check to run. Kept a day, so a phone that loses signal in the room still has it. */
export const useCheckTask = (id: string) =>
  useQuery({ queryKey: queryKeys.upkeep.task(id), queryFn: () => api<CheckTaskDTO>(endpoints.upkeep.task(id)), staleTime: 60_000, gcTime: 24 * 60 * 60_000 });

/** The Plan for a day: room by room, with the team's load. Polls, so a check handed in on a phone shows. "" is today. */
export const usePlan = (propertyId: string | undefined, date = "") =>
  useQuery({
    queryKey: queryKeys.upkeep.plan(propertyId ?? "", date),
    queryFn: () => api<CheckPlanDTO>(`${endpoints.upkeep.plan}${qs({ propertyId, date: date || undefined })}`),
    enabled: !!propertyId,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });

/** Staff: my checks today, and open ones to take. The app-wide prefetch reads it too, without polling. */
export const useMyChecks = (propertyId: string | undefined, opts: { poll?: boolean } = { poll: true }) =>
  useQuery({
    queryKey: queryKeys.upkeep.mine(propertyId ?? ""),
    queryFn: () => api<MyChecksDTO>(`${endpoints.upkeep.mine}${qs({ propertyId })}`),
    enabled: !!propertyId,
    refetchInterval: opts.poll ? 30_000 : false,
    staleTime: opts.poll ? 0 : 5 * 60_000,
  });

export const useCheckRules = (propertyId: string | undefined) =>
  useQuery({ queryKey: queryKeys.upkeep.rules(propertyId ?? ""), queryFn: () => api<CheckRulesDTO>(`${endpoints.upkeep.rules}${qs({ propertyId })}`), enabled: !!propertyId });

export const useRoomHistory = (roomId: string) =>
  useQuery({ queryKey: queryKeys.upkeep.roomHistory(roomId), queryFn: () => api<RoomHistoryDTO[]>(endpoints.upkeep.roomHistory(roomId)) });

export const useTaskDetail = (id: string | undefined) =>
  useQuery({ queryKey: queryKeys.upkeep.taskDetail(id ?? ""), queryFn: () => api<TaskDetailDTO>(endpoints.upkeep.taskAction(id!, "detail")), enabled: !!id });

export const useLaundry = (propertyId: string | undefined) =>
  useQuery({ queryKey: queryKeys.upkeep.laundry(propertyId ?? ""), queryFn: () => api<LaundryDTO>(`${endpoints.upkeep.laundry}${qs({ propertyId })}`), enabled: !!propertyId });

export const useLaundryBatch = (id: string) => useQuery({ queryKey: queryKeys.upkeep.batch(id), queryFn: () => api<LaundryBatchDTO>(endpoints.upkeep.batch(id)) });

export const useAccess = (propertyId: string | undefined) =>
  useQuery({ queryKey: queryKeys.access.list(propertyId ?? ""), queryFn: () => api<AccessDTO>(`${endpoints.access.list}${qs({ propertyId })}`), enabled: !!propertyId });

export const useAccessHistory = (roomId: string | undefined) =>
  useQuery({ queryKey: queryKeys.access.history(roomId ?? ""), queryFn: () => api<AccessHistoryDTO[]>(endpoints.access.history(roomId!)), enabled: !!roomId });

export const useChecklistTypes = (propertyId: string | undefined) =>
  useQuery({ queryKey: queryKeys.upkeep.checklistTypes(propertyId ?? ""), queryFn: () => api<ChecklistTypeDTO[]>(`${endpoints.upkeep.checklistTypes}${qs({ propertyId })}`), enabled: !!propertyId });

export const useTypeChecklist = (roomTypeId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.upkeep.typeChecklist(roomTypeId ?? ""),
    queryFn: () => api<TypeChecklistDTO>(`${endpoints.upkeep.checklists}${qs({ roomTypeId })}`),
    enabled: !!roomTypeId,
    placeholderData: keepPreviousData,
  });

export const useStarters = () =>
  useQuery({ queryKey: queryKeys.upkeep.starters, queryFn: () => api<StarterDTO[]>(endpoints.upkeep.starters), staleTime: Infinity });

export const useRoomSetup = (roomId: string | undefined) =>
  useQuery({ queryKey: queryKeys.upkeep.roomSetup(roomId ?? ""), queryFn: () => api<RoomSetupDTO>(endpoints.upkeep.roomSetup(roomId!)), enabled: !!roomId });
